'use client';

export type MetaPixelValue = string | number | boolean | null | undefined;
export type MetaPixelParameters = Record<string, MetaPixelValue>;

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: (...args: unknown[]) => void;
    __rupiqaMetaPixelInitialized?: boolean;
  }
}

const ATTRIBUTION_KEY = 'rupiqa_meta_attribution';
const ATTRIBUTION_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
] as const;

const STANDARD_EVENTS = new Set([
  'PageView',
  'ViewContent',
  'Lead',
  'CompleteRegistration',
  'Contact',
]);

function cleanParameters(parameters: MetaPixelParameters) {
  return Object.fromEntries(
    Object.entries(parameters).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );
}

export function persistMetaAttribution(search = window.location.search) {
  try {
    const current = new URLSearchParams(search);
    const existing = JSON.parse(window.localStorage.getItem(ATTRIBUTION_KEY) || '{}') as MetaPixelParameters;
    const next = { ...existing };

    ATTRIBUTION_FIELDS.forEach((field) => {
      const value = current.get(field);
      if (value) next[field] = value;
    });

    window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(next));
    return next;
  } catch {
    return {};
  }
}

export function getMetaAttribution() {
  try {
    return JSON.parse(window.localStorage.getItem(ATTRIBUTION_KEY) || '{}') as MetaPixelParameters;
  } catch {
    return {};
  }
}

export function trackMetaPixel(eventName: string, parameters: MetaPixelParameters = {}) {
  if (process.env.NEXT_PUBLIC_META_PIXEL_ENABLED_RUPIQA !== 'true') return;
  if (typeof window === 'undefined' || !window.fbq) return;
  const eventType = STANDARD_EVENTS.has(eventName) ? 'track' : 'trackCustom';
  window.fbq(eventType, eventName, cleanParameters({ ...getMetaAttribution(), ...parameters }));
}

export function trackLenderRedirect(
  destinationUrl: string,
  parameters: MetaPixelParameters = {}
) {
  try {
    const destination = new URL(destinationUrl, window.location.origin);
    trackMetaPixel('RedirectToLender', {
      ...parameters,
      destination_domain: destination.hostname,
    });
  } catch {
    trackMetaPixel('RedirectToLender', parameters);
  }
}
